import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, onAuthStateChanged } from '../auth';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import CreateGroupModal from '../components/groups/CreateGroupModal';
import PlusLeagueUpsellSheet from '../components/groups/PlusLeagueUpsellSheet';
import GroupPlanMatrix from '../components/groups/GroupPlanMatrix';
import GroupsEmptyState from '../components/groups/GroupsEmptyState';
import {
  acceptGroupInvite,
  canCreateGroup,
  declineGroupInvite,
  subscribeIncomingGroupInvites,
  subscribeMyGroupMemberships,
} from '../api/groupService';
import { fetchUserProfile } from '../api/userProfileService';
import { CHECKOUT_SOURCES } from '../constants/checkoutAttribution';
import { GROUP_FREE_JOIN_LIMIT_MESSAGE } from '../constants/groupLimits';
import { usePlusCheckout } from '../hooks/usePlusCheckout';
import { consumeOpenCreateGroupFlag, isJoinCapError } from '../utils/checkoutIntent';
import { typography } from '../design-system/tokens';

export default function GroupsPage() {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [invites, setInvites] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [upsellVariant, setUpsellVariant] = useState('groups_page');
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  const { startPlusCheckout, checkoutLoading } = usePlusCheckout({
    onError: (msg) => setError(msg),
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setAuthReady(true);
      if (firebaseUser?.uid) {
        try {
          const doc = await fetchUserProfile(firebaseUser.uid);
          setProfile(doc);
        } catch (_) {
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    return subscribeMyGroupMemberships(user.uid, setMemberships, () => setMemberships([]));
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    return subscribeIncomingGroupInvites(user.uid, setInvites, () => setInvites([]));
  }, [user?.uid]);

  const userCanCreate = canCreateGroup(profile);

  useEffect(() => {
    if (!userCanCreate) return;
    if (consumeOpenCreateGroupFlag()) {
      setCreateOpen(true);
    }
  }, [userCanCreate]);

  const openCreateFlow = () => {
    if (userCanCreate) {
      setCreateOpen(true);
      return;
    }
    setUpsellVariant('groups_page');
    setUpsellOpen(true);
  };

  const handleStartTrial = () => {
    const sourceMap = {
      groups_page: CHECKOUT_SOURCES.GROUPS_PAGE,
      join_cap: CHECKOUT_SOURCES.GROUP_JOIN_CAP,
      create_group: CHECKOUT_SOURCES.CREATE_GROUP_MODAL,
    };
    startPlusCheckout({
      source: sourceMap[upsellVariant] || CHECKOUT_SOURCES.GROUPS_PAGE,
      openCreateGroup: upsellVariant !== 'join_cap',
    });
  };

  const handleAcceptInvite = async (inviteId) => {
    setActionLoading(inviteId);
    setError('');
    try {
      const result = await acceptGroupInvite(inviteId);
      if (result?.groupId) navigate(`/groups/${result.groupId}`);
    } catch (err) {
      if (isJoinCapError(err?.message)) {
        setUpsellVariant('join_cap');
        setUpsellOpen(true);
      } else {
        setError(err?.message || 'Could not accept invite');
      }
    } finally {
      setActionLoading('');
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    setActionLoading(inviteId);
    setError('');
    try {
      await declineGroupInvite(inviteId);
    } catch (err) {
      setError(err?.message || 'Could not decline invite');
    } finally {
      setActionLoading('');
    }
  };

  if (!authReady) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-gray-500 text-sm font-bold">
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-xl font-black">Groups & Leagues</h1>
          <Alert tone="warning" className="text-sm font-bold">Sign in to view your groups.</Alert>
          <Button type="button" onClick={() => navigate('/signup?mode=login')}>Sign in</Button>
          <p className="text-xs text-gray-500">
            <Link to="/" className="text-violet-400 hover:text-violet-300">Back to home</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white px-4 py-8 sm:px-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`${typography.microWide} text-violet-400 mb-1`}>LockedIn</p>
            <h1 className="text-2xl font-black">My Groups</h1>
            <p className="text-xs text-gray-500 mt-1">
              Private leagues for your crew. {GROUP_FREE_JOIN_LIMIT_MESSAGE}
            </p>
          </div>
          {memberships.length > 0 && (
            <Button type="button" variant="admin" size="sm" onClick={openCreateFlow}>
              {userCanCreate ? 'Create league' : 'Start a league'}
            </Button>
          )}
        </header>

        {error && <Alert tone="error" className="text-sm font-bold">{error}</Alert>}

        {!userCanCreate && <GroupPlanMatrix />}

        {invites.length > 0 && (
          <Card variant="admin" className="p-4">
            <p className={`${typography.microWide} text-violet-300 mb-3`}>Pending invites</p>
            <ul className="space-y-3">
              {invites.map((inv) => (
                <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-800/80 pb-3 last:border-0 last:pb-0">
                  <div>
                    <p className="text-sm font-black">{inv.groupName}</p>
                    <p className="text-xs text-gray-500">From {inv.fromDisplayName}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="admin"
                      disabled={actionLoading === inv.id}
                      onClick={() => handleAcceptInvite(inv.id)}
                    >
                      Accept
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={actionLoading === inv.id}
                      onClick={() => handleDeclineInvite(inv.id)}
                    >
                      Decline
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card variant="admin" className="p-4">
          <p className={`${typography.microWide} text-gray-500 mb-3`}>
            Your groups ({memberships.length})
          </p>
          {memberships.length === 0 ? (
            <GroupsEmptyState
              hasInvites={invites.length > 0}
              userCanCreate={userCanCreate}
              onCreateLeague={openCreateFlow}
              onSeePlus={() => {
                setUpsellVariant('groups_page');
                setUpsellOpen(true);
              }}
            />
          ) : (
            <ul className="space-y-2">
              {memberships.map((m) => (
                <li key={m.id}>
                  <Link
                    to={`/groups/${m.id}`}
                    className="block rounded-xl border border-gray-800 bg-black/40 px-4 py-3 hover:border-violet-500/40 transition-colors"
                  >
                    <p className="text-sm font-black">{m.groupName || 'Group'}</p>
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">
                      {m.role || 'member'}{m.sport ? ` · ${m.sport}` : ''}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <p className="text-center text-xs text-gray-600">
          <Link to="/" className="text-violet-400 hover:text-violet-300">Back to Lockbox</Link>
        </p>
      </div>

      {userCanCreate && (
        <CreateGroupModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(groupId) => navigate(`/groups/${groupId}`)}
        />
      )}

      <PlusLeagueUpsellSheet
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        variant={upsellVariant}
        onStartTrial={handleStartTrial}
        checkoutLoading={checkoutLoading}
      />
    </div>
  );
}
